/**
 * Écrans du compte connecté : barre latérale partagée, navigation et raccourcis globaux.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { BarreLaterale, type Section } from './composants/BarreLaterale';
import { Menu, type Ancre } from './composants/Menu';
import { useActionsPaquets } from './donnees/actions-paquets';
import { useDonnees } from './donnees/contexte';
import type { useTheme } from './donnees/theme';
import { FournisseurLaterale } from './Coquille';
import { EcranEditeur } from './ecrans/EcranEditeur';
import { EcranGestionDesCartes } from './ecrans/EcranGestionDesCartes';
import { EcranStatistiques } from './ecrans/EcranStatistiques';
import { EcranPaquets } from './ecrans/EcranPaquets';
import { EcranReglagesDuCompte } from './ecrans/EcranReglagesDuCompte';
import { EcranReglagesDuPaquet } from './ecrans/EcranReglagesDuPaquet';
import { EcranSeance } from './seance/EcranSeance';
import { dansUnChamp } from './outils';
import { adresse, naviguer, useRoute, type Route } from './routes';
import { COMPTEURS_VIDES, type Compteurs } from '../seance/compteurs';
import { aplatir } from './donnees/arbre';

const LIENS: Record<Section, string> = {
  paquets: adresse({ ecran: 'paquets' }),
  'gestion-des-cartes': adresse({ ecran: 'gestion' }),
  statistiques: adresse({ ecran: 'statistiques' }),
  reglages: adresse({ ecran: 'reglages-compte' }),
};

const ROUTE_DE_SECTION: Record<Section, Route> = {
  paquets: { ecran: 'paquets' },
  'gestion-des-cartes': { ecran: 'gestion' },
  statistiques: { ecran: 'statistiques' },
  reglages: { ecran: 'reglages-compte' },
};

function sectionDe(route: Route): Section | null {
  switch (route.ecran) {
    case 'paquets':
      return route.paquet ? null : 'paquets';
    case 'gestion':
      return 'gestion-des-cartes';
    case 'statistiques':
      return 'statistiques';
    case 'reglages-compte':
      return 'reglages';
    default:
      return null;
  }
}

const CLE_DEPLIES = 'flashcard-deplies';

function lireDeplies(): Set<string> {
  try {
    return new Set(JSON.parse(window.localStorage.getItem(CLE_DEPLIES) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

export function Application({ theme }: { theme: ReturnType<typeof useTheme> }) {
  const route = useRoute();
  const { compte, services, jours } = useDonnees();
  const actions = useActionsPaquets();
  const [deplies, setDeplies] = useState<Set<string>>(lireDeplies);
  const [menuCompte, setMenuCompte] = useState<Ancre | null>(null);

  const deplier = (id: string, ouvert: boolean) => {
    setDeplies((actuels) => {
      const suivants = new Set(actuels);
      if (ouvert) suivants.add(id);
      else suivants.delete(id);
      try {
        window.localStorage.setItem(CLE_DEPLIES, JSON.stringify([...suivants]));
      } catch {
        // stockage indisponible
      }
      return suivants;
    });
  };

  const selection =
    route.ecran === 'paquets' || route.ecran === 'reglages-paquet' ? (route.paquet ?? null) : null;
  const paquetCourant =
    route.ecran === 'paquets' || route.ecran === 'editeur' || route.ecran === 'gestion'
      ? (route.paquet ?? null)
      : route.ecran === 'reglages-paquet' || route.ecran === 'seance'
        ? route.paquet
        : null;

  // N : nouvelle carte, hors d'un champ de saisie et sans modificateur.
  useEffect(() => {
    const clavier = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat || dansUnChamp(e.target)) return;
      if (route.ecran === 'seance' || route.ecran === 'editeur') return;
      if (document.querySelector('.fc-dialogue__voile, .fc-menu')) return;
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        naviguer(
          paquetCourant ? { ecran: 'editeur', paquet: paquetCourant } : { ecran: 'editeur' },
        );
      } else if (e.key === '/' && route.ecran !== 'gestion') {
        e.preventDefault();
        naviguer({ ecran: 'gestion' });
      }
    };
    window.addEventListener('keydown', clavier);
    return () => window.removeEventListener('keydown', clavier);
  }, [route, paquetCourant]);

  const compteursArbre = useMemo(() => {
    const resultat = new Map<string, Compteurs>();
    for (const n of aplatir(actions.noeuds)) {
      if (n.niveau === 0)
        resultat.set(
          n.paquet.id,
          jours.get(n.paquet.id)?.parPaquet.get(n.paquet.id) ?? COMPTEURS_VIDES,
        );
    }
    return resultat;
  }, [actions.noeuds, jours]);

  const laterale = (
    <BarreLaterale
      compte={compte.email}
      onCompte={(ancre) => setMenuCompte(ancre)}
      menuCompteOuvert={menuCompte !== null}
      onNouvelleCarte={() =>
        naviguer(paquetCourant ? { ecran: 'editeur', paquet: paquetCourant } : { ecran: 'editeur' })
      }
      section={sectionDe(route)}
      liens={LIENS}
      onNaviguer={(s) => naviguer(ROUTE_DE_SECTION[s])}
      arbre={{
        noeuds: actions.noeuds,
        compteurs: compteursArbre,
        selection,
        deplies,
        onDeplier: deplier,
        onOuvrir: (id) => naviguer({ ecran: 'paquets', paquet: id }),
        onLancer: (id) => naviguer({ ecran: 'seance', paquet: id }),
        onActions: actions.ouvrirMenu,
        onDeplacer: actions.deposer,
        menuOuvert: actions.menuOuvert,
      }}
      onNouveauPaquet={() => actions.ouvrirCreation(null)}
      onRechercher={(texte) =>
        naviguer(texte ? { ecran: 'gestion', recherche: texte } : { ecran: 'gestion' })
      }
    />
  );

  let ecran: ReactNode;
  switch (route.ecran) {
    case 'paquets':
      ecran = (
        <EcranPaquets
          selection={selection}
          deplies={deplies}
          onDeplier={deplier}
          noeuds={actions.noeuds}
          menuOuvert={actions.menuOuvert}
          onActions={actions.ouvrirMenu}
          onNouveauPaquet={() => actions.ouvrirCreation(null)}
        />
      );
      break;
    case 'seance':
      ecran = <EcranSeance key={route.paquet} paquetId={route.paquet} />;
      break;
    case 'editeur':
      ecran = (
        <EcranEditeur
          key={route.carteMere ?? 'nouvelle'}
          {...(route.carteMere ? { carteMereId: route.carteMere } : {})}
          {...(route.paquet ? { paquetDemande: route.paquet } : {})}
        />
      );
      break;
    case 'gestion':
      ecran = (
        <EcranGestionDesCartes
          key={`${route.paquet ?? 'tous'}:${route.recherche ?? ''}`}
          {...(route.paquet ? { paquetFiltre: route.paquet } : {})}
          {...(route.recherche ? { rechercheInitiale: route.recherche } : {})}
        />
      );
      break;
    case 'statistiques':
      ecran = <EcranStatistiques {...(route.paquet ? { paquetDemande: route.paquet } : {})} />;
      break;
    case 'reglages-compte':
      ecran = <EcranReglagesDuCompte theme={theme} />;
      break;
    case 'reglages-paquet':
      ecran = (
        <EcranReglagesDuPaquet
          key={route.paquet}
          paquetId={route.paquet}
          {...(route.onglet ? { onglet: route.onglet } : {})}
        />
      );
      break;
  }

  return (
    <FournisseurLaterale value={laterale}>
      {ecran}
      {actions.rendu}
      {menuCompte && (
        <Menu
          ancre={menuCompte}
          elements={[
            {
              type: 'titre',
              libelle: compte.email,
            },
            {
              type: 'action',
              libelle: 'Réglages du compte',
              icone: 'settings',
              onChoisir: () => naviguer({ ecran: 'reglages-compte' }),
            },
            { type: 'separateur' },
            {
              type: 'action',
              libelle: 'Se déconnecter',
              icone: 'log-out',
              onChoisir: () => void services.auth.deconnexion(),
            },
          ]}
          onFermer={() => setMenuCompte(null)}
        />
      )}
    </FournisseurLaterale>
  );
}
